import React, { useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { Field, useFormikContext } from "formik";
import { MobileDatePicker, MobileTimePicker } from "formik-mui-lab";
import set from "date-fns/set";
import { SessionDetailsItem } from "@app.automotus.io/components/SessionDetailsItem";
import { useUserProfile } from "@app.automotus.io/components/hooks";

export interface PrefilledDetailsValues {
  // Mirrors the field of the same name owned by EvidenceCaptureValues; kept as an
  // independent declaration here rather than importing across files for this one field.
  violationDateTime: Date | null;
  // Same reasoning -- read only for its presence/absence (see lastValidDateTimeRef's own
  // comment), never its shape, so a loose type is enough here.
  licensePlatePhoto: unknown;
}

export interface PrefilledDetailsProps {
  /**
   * The citation's jurisdiction (e.g. "Sacramento, CA" for SMF). Storybook-only override -- the
   * real route never passes this, and falls back instead to the signed-in officer's own
   * account.customer.addressCity/addressState (see accountFragment.ts). Renders an honest "-"
   * if neither resolves anything, rather than a guessed value.
   */
  city?: string;
  /**
   * The selected violation type's display label ("{code} — {description}"), resolved by the
   * caller from NoticeDetailsValues.violationCode -- passed as a prop rather than read directly
   * off Formik context because the field itself now stores the policy id, not the display
   * string (see NoticeDetails' ViolationTypeOption doc comment), so this section can't derive
   * the label on its own without also being handed the full options list.
   */
  violationCode?: string;
}

export const PrefilledDetails: React.FC<PrefilledDetailsProps> = ({ city, violationCode }) => {
  const { userProfile } = useUserProfile();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<PrefilledDetailsValues>();

  // Same "require both halves or show nothing" pattern CitationDetails.tsx already uses for the
  // identical addressCity/addressState pair sourced via a different relationship path -- a city
  // with no state (or vice versa) isn't a usable jurisdiction label.
  const customer = userProfile?.account?.customer;
  const jurisdictionCity =
    customer?.addressCity && customer?.addressState ? `${customer.addressCity}, ${customer.addressState}` : undefined;

  // Read by handleDateChange/handleTimeChange's own `base` fallback below instead of `new Date()`
  // whenever violationDateTime is currently null -- a mid-edit clear (an Invalid Date recovery
  // from a picker typo, see those handlers' own comment) would otherwise silently substitute
  // today's date once the officer repicks just one half, since the field's own value has nothing
  // left in it to merge onto by then.
  //
  // Only trusted while licensePlatePhoto is still present, though -- that photo is what actually
  // establishes this timestamp (see EvidenceCapture's own comment), and PrefilledDetails is never
  // remounted (unlike EvidenceCapture, keyed on evidenceResetKey), so this ref would otherwise
  // survive both Clear Form and a license-plate removal and hand the *next* citation attempt a
  // stale timestamp left over from this one, passing validation with an incorrect value. Once the
  // photo's gone, there's no real evidence left for this cached value to correspond to.
  const lastValidDateTimeRef = useRef<Date | null>(values.violationDateTime);
  useEffect(() => {
    if (values.violationDateTime) {
      lastValidDateTimeRef.current = values.violationDateTime;
    } else if (!values.licensePlatePhoto) {
      lastValidDateTimeRef.current = null;
    }
  }, [values.violationDateTime, values.licensePlatePhoto]);

  // MobileDatePicker/MobileTimePicker each edit only one half of the same violationDateTime
  // field, so a plain Formik-driven onChange (which would just overwrite the whole field with
  // whatever Date the picker returns) can't be trusted here -- confirmed against the actual
  // component, not assumed: selecting a day on the calendar face DOES preserve the existing
  // time of day (MUI merges internally there), but MobileTimePicker's "switch to text input"
  // fallback view does NOT preserve the existing date -- typing a time directly resets the date
  // portion to today. Handling the merge explicitly here, using date-fns' set() to combine the
  // newly-picked half with whatever's already in violationDateTime (or now, if it's still null),
  // makes the two fields behave consistently regardless of which UI path the officer uses.
  // setFieldValue alone never marks a field touched -- only a native blur or an explicit
  // setFieldTouched does. EvidenceCapture.validationSchema requires violationDateTime (AUT-5575
  // review), so clearing it without this would make the form invalid with no visible indication
  // of why: the error only renders once both touched and errors are set (confirmed directly
  // against formik-mui-lab's own field wrapper, not assumed), and Issue Citation's disabled
  // button means the officer can never trigger Formik's own submit-marks-everything-touched
  // fallback either.
  // An unparseable entry in either picker's text-input view (e.g. a typo mid-edit) arrives here
  // as an Invalid Date, not null -- MUI's useMaskedInput passes it straight through unless
  // ignoreInvalidInputs is set, which this field doesn't. An Invalid Date is truthy and not
  // nullish, so it would otherwise skip both the clear branch below and the `?? new Date()`
  // fallback, permanently poisoning the field: date-fns' set() can't repair an invalid base, so
  // every subsequent edit from either picker would keep merging onto it with no way back short
  // of Clear Form, which costs both photos and everything else on the form too.
  const handleDateChange = (newDate: Date | null) => {
    setFieldTouched("violationDateTime", true);
    if (!newDate || Number.isNaN(newDate.getTime())) {
      setFieldValue("violationDateTime", null);
      return;
    }
    const base = values.violationDateTime ?? lastValidDateTimeRef.current ?? new Date();
    setFieldValue(
      "violationDateTime",
      set(base, { year: newDate.getFullYear(), month: newDate.getMonth(), date: newDate.getDate() }),
    );
  };

  const handleTimeChange = (newTime: Date | null) => {
    setFieldTouched("violationDateTime", true);
    if (!newTime || Number.isNaN(newTime.getTime())) {
      setFieldValue("violationDateTime", null);
      return;
    }
    const base = values.violationDateTime ?? lastValidDateTimeRef.current ?? new Date();
    setFieldValue(
      "violationDateTime",
      set(base, { hours: newTime.getHours(), minutes: newTime.getMinutes(), seconds: newTime.getSeconds() }),
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Prefilled Details
      </Typography>
      {/* SessionDetailsItem hardcodes its own column span (Grid item xs={6} sm={3}) with no
          prop to override it, so forcing a strict 2-column layout here means overriding that
          from the container instead -- targeting the base .MuiGrid-item class (present at every
          breakpoint) rather than a specific breakpoint class, so no one sm={3}-derived rule can
          win by coming later in the stylesheet. */}
      <Grid
        container
        rowSpacing={2}
        columnSpacing={2}
        sx={{ "& > .MuiGrid-item": { flexBasis: "50% !important", maxWidth: "50% !important" } }}
      >
        <SessionDetailsItem
          title="Violation Date"
          // label duplicates the title visually (a small floating label inside the field,
          // underneath SessionDetailsItem's own bold heading above it) -- accepted deliberately
          // rather than fighting MUI's picker internals: passing inputProps={{ "aria-label": ... }}
          // instead would silently clobber the picker's own inputProps (notably readOnly, which
          // is what forces date/time entry through the picker dialog instead of free-typing),
          // since formik-mui-lab spreads textField after its renderInput params with no merge.
          contentChildren={
            <Field
              component={MobileDatePicker}
              name="violationDateTime"
              label="Violation Date"
              onChange={handleDateChange}
              textField={{ variant: "standard", fullWidth: true, placeholder: "-" }}
            />
          }
        />
        <SessionDetailsItem
          title="Violation Time"
          contentChildren={
            <Field
              component={MobileTimePicker}
              name="violationDateTime"
              label="Violation Time"
              onChange={handleTimeChange}
              textField={{ variant: "standard", fullWidth: true, placeholder: "-" }}
            />
          }
        />
        <SessionDetailsItem title="Jurisdiction" content={city || jurisdictionCity || "-"} />
        <SessionDetailsItem title="Violation Code" content={violationCode || "-"} />
        <SessionDetailsItem title="Issuing Officer" content={userProfile?.fullName || "-"} />
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Date and time are captured automatically but can be edited if needed. Officer is captured automatically. The
        violation code is set below.
      </Typography>
    </Paper>
  );
};

export default PrefilledDetails;
